import React, { useState } from 'react';
import { X, Send, Building2, Phone, User, Briefcase, Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Feriados Nacionais Brasileiros e do Distrito Federal (2024-2026)
const HOLIDAYS = [
  // 2024
  '2024-01-01', // Confraternização Universal
  '2024-02-12', // Carnaval
  '2024-02-13', // Carnaval
  '2024-03-29', // Sexta-feira Santa
  '2024-04-21', // Tiradentes / Aniversário de Brasília
  '2024-05-01', // Dia do Trabalho
  '2024-05-30', // Corpus Christi
  '2024-09-07', // Independência
  '2024-10-12', // Nossa Senhora Aparecida
  '2024-11-02', // Finados
  '2024-11-15', // Proclamação da República
  '2024-11-30', // Dia do Evangélico (DF)
  '2024-12-25', // Natal
  // 2025
  '2025-01-01', // Confraternização Universal
  '2025-03-03', // Carnaval
  '2025-03-04', // Carnaval
  '2025-04-18', // Sexta-feira Santa
  '2025-04-21', // Tiradentes / Aniversário de Brasília
  '2025-05-01', // Dia do Trabalho
  '2025-06-19', // Corpus Christi
  '2025-09-07', // Independência
  '2025-10-12', // Nossa Senhora Aparecida
  '2025-11-02', // Finados
  '2025-11-15', // Proclamação da República
  '2025-11-30', // Dia do Evangélico (DF)
  '2025-12-25', // Natal
  // 2026
  '2026-01-01', // Confraternização Universal
  '2026-02-16', // Carnaval
  '2026-02-17', // Carnaval
  '2026-04-03', // Sexta-feira Santa
  '2026-04-21', // Tiradentes / Aniversário de Brasília
  '2026-05-01', // Dia do Trabalho
  '2026-06-04', // Corpus Christi
  '2026-09-07', // Independência
  '2026-10-12', // Nossa Senhora Aparecida
  '2026-11-02', // Finados
  '2026-11-15', // Proclamação da República
  '2026-11-30', // Dia do Evangélico (DF)
  '2026-12-25', // Natal
];

const LeadCaptureModal = ({ isOpen, onClose, preSelectedService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    area: '',
    service: preSelectedService,
    preferredDate: null,
    preferredTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Atualiza o serviço quando preSelectedService muda
  React.useEffect(() => {
    if (preSelectedService) {
      setFormData(prev => ({ ...prev, service: preSelectedService }));
    }
  }, [preSelectedService]);

  const serviceOptions = [
    'Diagnóstico BIM',
    'Projetos de Arquitetura',
    'Projetos de Engenharia',
    'Projetos de Infraestrutura',
    'As Built',
    'Planejamento 4D',
    'Orçamentação 5D',
    'Coordenação BIM',
    'Modelagem BIM',
    'Gêmeos Digitais',
    'Consultoria BIM',
    'Outro'
  ];

  const areaOptions = [
    'Arquitetura',
    'Engenharia Civil',
    'Engenharia Elétrica',
    'Engenharia Mecânica',
    'Construção Civil',
    'Incorporação',
    'Órgão Público',
    'Indústria',
    'Infraestrutura',
    'Outro'
  ];

  const timeOptions = [
    { value: '08:00 - 10:00', label: '08:00 às 10:00' },
    { value: '10:00 - 12:00', label: '10:00 às 12:00' },
    { value: '13:00 - 15:00', label: '13:00 às 15:00' },
    { value: '15:00 - 17:00', label: '15:00 às 17:00' }
  ];

  const isHoliday = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return HOLIDAYS.includes(dateStr);
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateDisabled = (date) => {
    return isWeekend(date) || isHoliday(date) || isPastDate(date);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    for (let i = 0; i < startingDay; i++) {
      const prevDate = new Date(year, month, -startingDay + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days to complete the grid
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const formatDateDisplay = (date) => {
    if (!date) return '';
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('pt-BR', options);
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateSelect = (date) => {
    if (!isDateDisabled(date)) {
      setFormData({ ...formData, preferredDate: date });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.preferredDate) {
      toast.error('Selecione uma data no calendário');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads/diagnostic`, {
        ...formData,
        preferredDay: formatDateDisplay(formData.preferredDate)
      });
      
      if (response.data.success) {
        toast.success('Solicitação enviada com sucesso! Entraremos em contato em breve.');
        onClose();
        setFormData({
          name: '',
          phone: '',
          company: '',
          area: '',
          service: '',
          preferredDate: null,
          preferredTime: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const days = getDaysInMonth(currentMonth);
  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-2xl w-full relative max-h-[95vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            {preSelectedService ? 'Solicitar Orçamento' : 'Diagnóstico BIM Gratuito'}
          </h2>
          <p className="text-gray-400">
            {preSelectedService 
              ? `Preencha os dados abaixo para solicitar um orçamento de ${preSelectedService}.`
              : 'Preencha os dados abaixo para agendar uma reunião sobre implantação BIM.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Campo de Serviço */}
          <div>
            <Label htmlFor="service" className="text-white flex items-center gap-2">
              <Briefcase size={16} className="text-red-500" />
              Serviço de Interesse *
            </Label>
            <select
              id="service"
              value={formData.service}
              onChange={(e) => setFormData({ ...formData, service: e.target.value })}
              className="w-full bg-black border border-zinc-700 text-white mt-2 rounded-md px-3 py-2"
              required
              disabled={isSubmitting}
            >
              <option value="">Selecione o serviço</option>
              {serviceOptions.map((service) => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-white flex items-center gap-2">
                <User size={16} className="text-red-500" />
                Nome Completo *
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-black border-zinc-700 text-white mt-2"
                placeholder="Seu nome completo"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-white flex items-center gap-2">
                <Phone size={16} className="text-red-500" />
                Telefone com DDD *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-black border-zinc-700 text-white mt-2"
                placeholder="(00) 00000-0000"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company" className="text-white flex items-center gap-2">
                <Building2 size={16} className="text-red-500" />
                Empresa ou Instituição *
              </Label>
              <Input
                id="company"
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="bg-black border-zinc-700 text-white mt-2"
                placeholder="Nome da empresa"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <Label htmlFor="area" className="text-white flex items-center gap-2">
                <Briefcase size={16} className="text-red-500" />
                Área de Atuação *
              </Label>
              <select
                id="area"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                className="w-full bg-black border border-zinc-700 text-white mt-2 rounded-md px-3 py-2"
                required
                disabled={isSubmitting}
              >
                <option value="">Selecione sua área</option>
                {areaOptions.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Calendar Section */}
          <div className="mt-4">
            <Label className="text-white flex items-center gap-2 mb-3">
              <Calendar size={16} className="text-red-500" />
              Selecione a Data *
            </Label>
            
            <div className="bg-black border border-zinc-700 rounded-lg p-4">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-zinc-800 rounded-full transition-colors text-gray-400 hover:text-white"
                >
                  <ChevronLeft size={20} />
                </button>
                <h3 className="text-white font-semibold text-lg">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h3>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-zinc-800 rounded-full transition-colors text-gray-400 hover:text-white"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Weekday Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day, index) => (
                  <div
                    key={day}
                    className={`text-center text-sm font-medium py-2 ${
                      index === 0 || index === 6 ? 'text-red-400' : 'text-gray-400'
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {days.map(({ date, isCurrentMonth }, index) => {
                  const disabled = isDateDisabled(date);
                  const isSelected = isSameDay(date, formData.preferredDate);
                  const isToday = isSameDay(date, new Date());
                  
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleDateSelect(date)}
                      disabled={disabled || !isCurrentMonth}
                      className={`
                        p-2 text-sm rounded-lg transition-all
                        ${!isCurrentMonth ? 'text-zinc-700 cursor-not-allowed' : ''}
                        ${isCurrentMonth && disabled ? 'text-zinc-600 cursor-not-allowed line-through' : ''}
                        ${isCurrentMonth && !disabled ? 'text-white hover:bg-red-600/30 cursor-pointer' : ''}
                        ${isSelected ? 'bg-red-600 text-white hover:bg-red-700' : ''}
                        ${isToday && !isSelected ? 'ring-1 ring-red-500' : ''}
                        ${isHoliday(date) && isCurrentMonth ? 'bg-orange-900/30' : ''}
                      `}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-zinc-800 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-600 rounded"></div>
                  <span className="text-gray-400">Selecionado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-900/50 rounded"></div>
                  <span className="text-gray-400">Feriado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border border-zinc-600 rounded line-through"></div>
                  <span className="text-gray-400">Indisponível</span>
                </div>
              </div>

              {/* Selected Date Display */}
              {formData.preferredDate && (
                <div className="mt-4 p-3 bg-red-600/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">
                    📅 Data selecionada: <span className="text-white font-medium">{formatDateDisplay(formData.preferredDate)}</span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Time Selection */}
          <div>
            <Label className="text-white flex items-center gap-2 mb-3">
              <Clock size={16} className="text-red-500" />
              Selecione o Horário *
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {timeOptions.map((time) => (
                <button
                  key={time.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, preferredTime: time.value })}
                  className={`
                    p-3 rounded-lg border text-sm font-medium transition-all
                    ${formData.preferredTime === time.value
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'bg-black border-zinc-700 text-gray-300 hover:border-red-500 hover:text-white'
                    }
                  `}
                >
                  {time.label}
                </button>
              ))}
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-red-600 hover:bg-red-700 text-white mt-6"
            disabled={isSubmitting || !formData.preferredDate || !formData.preferredTime}
          >
            {isSubmitting ? (
              'Enviando...'
            ) : (
              <>
                <Send className="mr-2" size={18} />
                Solicitar Agendamento
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">
          Ao enviar, você concorda em receber contato da equipe AJ BIM.
        </p>
      </div>
    </div>
  );
};

export default LeadCaptureModal;
